import { useEffect } from "react";
import { Nav } from "react-bootstrap";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ACCESS_CONFIG } from "../strings";
import { hasAccess } from "../../services/access";
import { useDialog } from "../../hooks/useDialog";
import { showError } from "../../services/toast-service";
import { setMissingInfoCount } from "../../store/slices/missingInfo";
import { fetchMissingInfoCount } from "../../services/api-declaration";
import { useAgentAssignment } from "../../context/agent-assignment-context";
import { useTranslation } from "react-i18next";

const IconWrapper = styled.div`
  position: relative;
  width: 50px;
`;
const SidebarIcon = styled.img`
  width: 24px;
  height: 24px;
`;
const ItemText = styled.div`
  font-size: 18px;
`;
const BubbleDiv = styled.div`
  position: absolute;
  width: 18px;
  height: 18px;
  border: 2px solid white;
  border-radius: 10px;
  right: 4px;
  top: -7px;
  background: #fd7e14;
  display: flex;
  justify-content: center;
  text-align: center;
  align-items: center;
`;

const AGENT_ROUTES = ["agent", "offer-followup-agent"];

const Sidebar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showConfirmationDialogBox } = useDialog();
  const { currentAssignment, releaseCurrentAssignment } = useAgentAssignment();
  const { ALL_ROLES, ADMIN_TIER, ONLY_SUPER_ADMIN, MARKETING_MANAGER_TIER } =
    ACCESS_CONFIG;
  const leadInfoCount = useSelector((state) => state.missingInfo.leadCount);
  const categoryInfoCount = useSelector(
    (state) => state.missingInfo.categoryCount
  );
  const customerInfoCount = useSelector(
    (state) => state.missingInfo.customerCount
  );
  const hubspotInfoCount = useSelector(
    (state) => state.missingInfo.hubspotCount
  );

  const location = useLocation(); // Get the current route location

  useEffect(() => {
    (async function () {
      if (hasAccess(ADMIN_TIER)) {
        try {
          const resp = await fetchMissingInfoCount();
          const countData = resp.data.data;
          dispatch(setMissingInfoCount(countData));
        } catch (error) {
          console.log("err", error);
        }
      }
    })();
    // eslint-disable-next-line
  }, []);

  let sidebarItems = [
    {
      name: t("sidebar.dashboard"),
      icon: "/icons/home.svg",
      link: "",
      parentLink: "dashboard",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: "Funnels",
      icon: "/icons/feedback.svg",
      link: "funnels",
      parentLink: "funnels",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: t("sidebar.templates"),
      icon: "/icons/folder.svg",
      link: "templates",
      parentLink: "templates",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: t("sidebar.customers"),
      icon: "/icons/folder-share.svg",
      link: "customers",
      parentLink: "customers",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: "Leads",
      icon: "/icons/inbox.svg",
      link: "leads",
      parentLink: "leads",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: t("sidebar.categories"),
      icon: "/icons/diary.svg",
      link: "categories",
      parentLink: "categories",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: "Keywords",
      icon: "/icons/keyword.svg",
      link: "keywords",
      parentLink: "keywords",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: t("sidebar.gallery"),
      icon: "/icons/image.svg",
      link: "gallery",
      parentLink: "gallery",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: t("sidebar.tags"),
      icon: "/icons/encyclopedia.svg",
      link: "tags",
      parentLink: "tags",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: t("sidebar.users"),
      icon: "/icons/user.svg",
      link: "user",
      parentLink: "user",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: t("sidebar.settings"),
      icon: "/icons/settings.svg",
      link: `settings/profile`,
      parentLink: "settings",
      access: MARKETING_MANAGER_TIER
    },
    ...(hasAccess(ONLY_SUPER_ADMIN)
      ? [
          {
            name: t("sidebar.siteSettings"),
            icon: "/icons/site-settings.svg",
            link: `site-settings`,
            parentLink: "site-settings",
            access: ONLY_SUPER_ADMIN
          }
        ]
      : []),
    {
      name: t("sidebar.leadAgent"),
      icon: "/icons/agent.svg",
      link: `agent`,
      parentLink: "agent",
      access: ALL_ROLES
    },
    {
      name: "Offer Follow-Up",
      icon: "/icons/agent.svg",
      link: "offer-followup-agent",
      parentLink: "offer-followup-agent",
      access: ALL_ROLES
    },
    {
      name: "Leadservice Panel",
      icon: "/icons/leadservice-panel.svg",
      link: `leadservice-panel/leadservice`,
      parentLink: "leadservice-panel",
      access: MARKETING_MANAGER_TIER
    },
    ...(hasAccess(ADMIN_TIER)
      ? [
          {
            name: t("funnelsEvaluation"),
            icon: "/icons/evaluation.svg",
            link: `evaluation/overview?dateRange=L14D`,
            parentLink: "evaluation",
            access: ADMIN_TIER
          },
          {
            name: t("sidebar.fulfillmentKpis"),
            icon: "/icons/target.svg",
            link: `fulfillment-kpis`,
            parentLink: "fulfillment-kpis",
            access: ADMIN_TIER
          },
          {
            name: "Information Required",
            icon: "/icons/missing.svg",
            link: `missing-info/lead`,
            parentLink: "missing-info",
            showPopup:
              Boolean(categoryInfoCount) ||
              Boolean(leadInfoCount) ||
              Boolean(customerInfoCount) ||
              Boolean(hubspotInfoCount),
            access: ADMIN_TIER
          }
        ]
      : []),
    {
      name: t("creativeDashboard"),
      icon: "/icons/evaluation.svg",
      link: `ads/evaluation/overview`,
      parentLink: "ads/evaluation",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: t("tools"),
      icon: "/icons/tools.svg",
      link: `tools/fb-duplicator`,
      parentLink: "tools",
      access: MARKETING_MANAGER_TIER
    },
    {
      name: t("sidebar.splitTests"),
      icon: "/icons/split-test.svg",
      link: `split-tests/lead`,
      parentLink: "split-tests",
      access: MARKETING_MANAGER_TIER
    }
    // { name: t("sidebar.advertisements"), icon: "/icons/target.svg", link: "" },
  ];
  sidebarItems = sidebarItems.filter(({ access }) => hasAccess(access));

  const getActiveAgentRoute = () =>
    AGENT_ROUTES.find(
      (route) =>
        location.pathname === `/dashboard/${route}` ||
        location.pathname.startsWith(`/dashboard/${route}/`)
    );

  const renderAgentSwitchMessage = (targetName) => (
    <div>{t("agentRouteSwitch.message", { target: targetName })}</div>
  );

  const handleAgentSwitchResponse = async (action, targetPath) => {
    if (!action) return;

    try {
      await releaseCurrentAssignment();
      navigate(`/dashboard/${targetPath}`);
    } catch (error) {
      console.log("Error in switching agent route", error);
      showError(
        error?.response?.data?.message || t("agentRouteSwitch.releaseError")
      );
    }
  };

  const handleTabClick = (event, item) => {
    const { disabled, parentLink, link, name } = item;

    if (disabled) {
      event.preventDefault();
      alert("This tab is currently disabled due to an ongoing call.");
      return;
    }

    const activeAgentRoute = getActiveAgentRoute();
    const isLeavingActiveAgentPanel =
      activeAgentRoute && parentLink !== activeAgentRoute;

    if (currentAssignment?.isInCall && isLeavingActiveAgentPanel) {
      event.preventDefault();
      showError(t("agentRouteSwitch.activeCallError"));
      return;
    }

    const shouldConfirmAgentSwitch =
      AGENT_ROUTES.includes(activeAgentRoute) &&
      AGENT_ROUTES.includes(parentLink) &&
      activeAgentRoute !== parentLink &&
      currentAssignment?.isAssigned;

    if (!shouldConfirmAgentSwitch) return;

    event.preventDefault();
    showConfirmationDialogBox({
      title: t("agentRouteSwitch.title"),
      dialogType: "warning",
      dialogMessage: renderAgentSwitchMessage(name),
      acceptLabel: t("agentRouteSwitch.continue"),
      declineLabel: t("agentRouteSwitch.stay"),
      responseAction: (action) => handleAgentSwitchResponse(action, link)
    });
  };
  return (
    <>
      <div className="sidebar">
        {sidebarItems.map((item, index) => (
          <Nav.Link
            active={new RegExp(
              `^/dashboard/${item.parentLink}(?:[/?].*)?$`
            ).test(location.pathname)}
            key={index}
            as={Link}
            className="item-wrapper"
            style={
              item.disabled
                ? {
                    opacity: 0.6,
                    cursor: "not-allowed"
                  }
                : {}
            }
            to={item.disabled ? "#" : item.link}
            onClick={(event) => handleTabClick(event, item)}
          >
            <IconWrapper className="d-flex align-items-center justify-content-center">
              {item.showPopup && <BubbleDiv />}
              <SidebarIcon alt={`item-${index}`} src={item.icon} />
            </IconWrapper>
            <ItemText>{item.name}</ItemText>
          </Nav.Link>
        ))}
      </div>
    </>
  );
};
export default Sidebar;
